import { AuthenticateMessagePayload } from 'embedded/api/in/messages/AuthenticateMessagePayload';
import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import { SetPositionsMessagePayload } from 'embedded/api/in/messages/SetPositionsMessagePayload';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { GlueInInteropMethodNames } from 'app/glue/interop/types';
import { SetOrderIdsMessagePayload } from 'embedded/api/in/messages/SetOrderIdsMessagePayload';
import { LogoutMessagePayload } from 'embedded/api/in/messages/LogoutMessagePayload';
import { SetPortfolioMessagePayload } from 'embedded/api/in/messages/SetPortfolioMessagePayload';

export interface InMethodProxy<T> {
    name: GlueInInteropMethodNames;
    displayName: string;
    description: string;
    accepts?: string;
    handler: (
        embeddedApiService: EmbeddedApiService,
        args: T
    ) => Promise<string>;
}

const authenticate: InMethodProxy<AuthenticateMessagePayload> = {
    name: 'Authenticate',
    displayName: 'Authenticate Indigo',
    description: 'Authenticates Indigo with a JWT token',
    accepts:
        'Composite: {String token, String application, String applicationVersion, String? machineId, String? correlationId}',
    handler: (embeddedApiService, payload) => {
        return embeddedApiService.receiveMessage('AUTHENTICATE', payload);
    },
};

const setPositions: InMethodProxy<SetPositionsMessagePayload> = {
    name: 'SetPositions',
    displayName: 'Set Positions',
    description: 'Set Indigo Positions',
    accepts:
        'Composite: {String symbology, Composite: {String: symbol, String?: side, number?: qty}[] positions, String? correlationId}',
    handler: (embeddedApiService, payload) => {
        return embeddedApiService.receiveMessage('SET_POSITIONS', payload);
    },
};

const setInstrument: InMethodProxy<{
    symbology: SymbologyValues;
    symbol: string;
    side?: 'B' | 'S' | 'SS';
    qty?: number;
}> = {
    name: 'SetInstrument',
    displayName: 'Set Instrument',
    description: 'Set Indigo Instrument',
    accepts:
        'Composite: {String symbology, String: symbol, String?: side, number?: qty, String? correlationId}',
    handler: (embeddedApiService, payload) => {
        const apiPayload: SetPositionsMessagePayload = {
            symbology: payload.symbology,
            positions: [
                {
                    symbol: payload.symbol,
                    side: payload.side,
                    qty: payload.qty,
                },
            ],
        };
        return embeddedApiService.receiveMessage('SET_POSITIONS', apiPayload);
    },
};

const setOrders: InMethodProxy<SetOrderIdsMessagePayload> = {
    name: 'SetOrders',
    displayName: 'Set Orders',
    description: 'Set Indigo Orders',
    accepts: 'Composite: {String[] orderIds, String? correlationId}',
    handler: (embeddedApiService, payload) => {
        return embeddedApiService.receiveMessage('SET_ORDER_IDS', payload);
    },
};

const setPortfolio: InMethodProxy<SetPortfolioMessagePayload> = {
    name: 'SetPortfolio',
    displayName: 'Set Portfolio',
    description: 'Set Indigo Portfolio',
    accepts:
        'Composite: {String symbology, String[] orderIds, Composite: {String: symbol, String?: side, number?: qty}[] positions, String? correlationId}',
    handler: (embeddedApiService, payload) => {
        return embeddedApiService.receiveMessage('SET_PORTFOLIO', payload);
    },
};

const logout: InMethodProxy<LogoutMessagePayload> = {
    name: 'Logout',
    displayName: 'Logout',
    description: 'Logout from Indigo',
    handler: (embeddedApiService, payload) => {
        return embeddedApiService.receiveMessage('LOGOUT', payload);
    },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const inMethods: InMethodProxy<any>[] = [
    authenticate,
    logout,
    setPositions,
    setInstrument,
    setOrders,
    setPortfolio,
];
